import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import {
  BookOpenIcon,
  Cog6ToothIcon,
  DocumentIcon
} from "@heroicons/react/24/outline";
import {
  ArrowLeftStartOnRectangleIcon,
  Bars3Icon,
  CalendarDaysIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  UserIcon,
  UserGroupIcon
} from "@heroicons/react/24/solid";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition
} from "@headlessui/react";
import { VisibilityGuard } from "@lxp-app/utils/visibilityGuard";
import CalendarAgendaCard from "../../pages/landing-page/calendar-widget/calendarAgendaCard";
import Notification from "../../components/molecules/notification/notificationNew";
import useTracking from "../../components/molecules/analytics/hooks/useTracking";
import LogoutConfirmationModal from "../../components/molecules/modal-children/logoutConfirmModal";
import Modal from "../../components/atoms/modal";
import SearchBox from "./searchBox";
import Switcher from "./switcher-new";
import { RootState } from "../../store/store";
import { useLazyGetProfileForSupervisorQuery } from "../../pages/admin-dashboard/slices/api/adminDashboardApiSlice";
import UserRole from "@lxp-app/utils/roles";
import glxLogo from "../../assets/svg/glxLogo.svg";
import glxLogoDark from "../../assets/svg/glxDarkLogo.svg";
import hitachiLogo from "../../assets/svg/hitachiLogo.svg";
import TMobileLogo from "../../assets/png/tmobile-logo.png";
import glLogo from "../../assets/png/glLogo.png";
import hitachiDarkLogo from "../../assets/svg/hitachiDarkLogo.svg";
import glDarkLogo from "../../assets/svg/GlDarkLogo.svg";
import TrainingCalendarIcon from "../../assets/svg/Training Calendar Icon.svg";
import MyCalendarIcon from "../../assets/svg/My Calendar.svg";
import ActivityDashboardIcon from "../../assets/svg/Activity Dashboard.svg";
import ManagerDashboardIcon from "../../assets/svg/Manager Dashboard.svg";
import CafeLoungeIcon from "../../assets/svg/Cafe Lounge.svg";
import QuizumiIcon from "../../assets/svg/quizumi.svg";
import GLXInterviewIcon from "../../assets/svg/glx_interview.svg";
import FeaturedPrograms from "../../assets/svg/featured.svg";
import GLXRolePlayIcon from "../../assets/svg/role_play_simulator.svg";
import glqLogo from "@assessment/assets/images/langingpage/logo_image.png";
import LearningWallIcon from "../../assets/svg/Learning_wall.svg";
import { getAuthoringLibraryLabel } from "@lxp-app/utils/authoringLibraryResources/localizeAuthoringLibraryLabels";

type PopoverProps = {
  open?: () => void;
  close?: () => void;
};

type HeaderProps = {
  displayNotification?: boolean;
  assessment?: boolean;
  displaySearch?: boolean;
  displayExplore?: boolean;
  displayCalendar?: boolean;
};

export default function Header({
  displayNotification = true,
  displayCalendar = true,
  assessment = false,
  displaySearch = true,
  displayExplore = true
}: HeaderProps) {
  const [track] = useTracking();
  const location = useLocation();

  const { keycloak } = useKeycloak();
  const [searchParams] = useSearchParams();
  const [hasReportees, setHasReportees] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [logoutPopUpOpen, setLogoutPopUpOpen] = useState<boolean>(false);
  const { isDarkMode } = useSelector((state: any) => state.DarkmodeSlice);
  const user = useSelector((state: RootState) => state.UserInfoSlice);
  const language = user?.profile?.preferences?.language || "en";
  const currentOrg = user.org || "gl-india";
  const orgRoutes = useSelector(
    (state: RootState) => state.UserInfoSlice.orgRoutes
  );
  const subscriberId = useSelector(
    (state: RootState) => state.UserInfoSlice.profile.subscriberIdNovu as string
  );
  const tenantConfig = useSelector(
    (state: RootState) => state.AppMetaDataSlice.tenantConfig
  );

  const authorRoles = [
    UserRole.ASSESSMENT_ADMIN,
    UserRole.ASSESSMENT_CREATOR,
    UserRole.ADMIN,
    UserRole.REVIEWER,
    UserRole.CREATOR,
    UserRole.RECRUITER
  ];

  const exploreMenuItems = [
    {
      title: "Training Calendar",
      icon: TrainingCalendarIcon,
      to: "/calendar/calendar-dashboard-global",
      disable: !tenantConfig?.header?.exploreMenu?.trainingCalendar
    },
    {
      title: "My Calendar",
      icon: MyCalendarIcon,
      to: "/calendar/calendar-dashboard-learner",
      disable: !tenantConfig?.header?.exploreMenu?.myCalendar
    },
    {
      title: "Activity Dashboard",
      icon: ActivityDashboardIcon,
      to: "/profile/activity-dashboard/overview-dashboard",
      disable: !tenantConfig?.header?.exploreMenu?.activityDashboard
    },
    {
      title: "Manager Dashboard",
      icon: ManagerDashboardIcon,
      to: "/profile/manager-dashboard",
      disable:
        !tenantConfig?.header?.exploreMenu?.managerDashboard || !hasReportees
    },
    // {
    //   title: "Assigned Learning",
    //   icon: AssignedLearningIcon,
    //   to: "/profile/activity-dashboard/assigned-content"
    // },
    {
      title: "Café Lounge",
      icon: CafeLoungeIcon,
      to: "/cafe-lounge",
      new: false,
      disable: !tenantConfig?.header?.exploreMenu?.cafeLounge
    },
    {
      title: "Socrato",
      icon: QuizumiIcon,
      to: "/socrato",
      new: false,
      openInNewTab: false,
      disable:
        !user.profile.role?.includes(UserRole.ADMIN) ||
        !tenantConfig?.header?.exploreMenu?.socrato
    },
    {
      title: "Featured Programs",
      icon: FeaturedPrograms,
      to: "/featured-programs",
      new: true,
      openInNewTab: false,
      disable: !tenantConfig?.header?.exploreMenu?.featuredPrograms
    },
    {
      title: "GLX Interview Mirror",
      icon: GLXInterviewIcon,
      to: "/ai-interview",
      new: true,
      openInNewTab: false,
      disable: !tenantConfig?.header?.exploreMenu?.aiInterview
    },
    {
      title: "Role Play Simulator",
      icon: GLXRolePlayIcon,
      to: "/role-play-simulator",
      new: true,
      openInNewTab: false,
      disable:
        !user.profile.role?.includes(UserRole.LEADERSHIP) ||
        !tenantConfig?.header?.exploreMenu?.rolePlaySimulator
    },
    {
      title: "Learning Wall",
      icon: LearningWallIcon,
      to: "/learning-canvas",
      new: true
    }
  ];

  const [fetchReportees] = useLazyGetProfileForSupervisorQuery();

  useEffect(() => {
    (async () => {
      if (user?.profile?.id) {
        fetchReportees(user?.profile?.id)
          .unwrap()
          .then((reporteesData: any) => {
            setHasReportees(
              reporteesData?.getProfileForSupervisor?.data?.totalCount > 0 ||
                reporteesData.reporteesData?.getProfileForSupervisor?.data
                  ?.myReportees?.length > 0
            );
          });
      }
    })();
  }, [user?.profile?.id]);

  const notSuperAdmin = !user?.profile?.isSuperAdmin;

  const hasAssessmentRecOrgs = user?.profile?.hasAssessmentRecOrgs;

  const hasAuthorAccess = user?.profile?.role?.some((role: UserRole) =>
    authorRoles.includes(role)
  );
  const isReportAdmin = user?.profile?.role?.includes(UserRole.REPORT_ADMIN);

  const menuItems: any[] = hasAssessmentRecOrgs
    ? [
        {
          icon: PencilSquareIcon,
          name: "Content Admin",
          href: "/authoring",
          disabled: !hasAuthorAccess
        },
        {
          icon: Cog6ToothIcon,
          name: getAuthoringLibraryLabel("myPreferences", language),
          href: "/my-preferences",
          disabled: false
        },
        {
          icon: ArrowLeftStartOnRectangleIcon,
          name: getAuthoringLibraryLabel("logout", language),
          href: "/",
          disabled: !(keycloak && keycloak.authenticated)
        }
      ]
    : [
        { icon: UserIcon, name: "Profile", href: "/profile", disabled: false },
        {
          icon: PencilSquareIcon,
          name: "Content Admin",
          href: "/authoring",
          disabled: !hasAuthorAccess
        },
        {
          icon: UserGroupIcon,
          name: "Super Admin",
          href: "super-admin",
          disabled: notSuperAdmin
        },
        {
          icon: DocumentIcon,
          name: "Reports",
          href: "reports",
          disabled: !isReportAdmin
        },
        {
          icon: ArrowLeftStartOnRectangleIcon,
          name: getAuthoringLibraryLabel("logout", language),
          href: "/",
          disabled: !(keycloak && keycloak.authenticated)
        }
      ];

  const divider = () => {
    return (
      <div
        aria-hidden="true"
        className="h-10 w-px bg-gradient-to-t from-[#f5f5f5] via-[#c8cad3] to-[#f5f5f5] dark:from-[#242526] dark:via-[#808080] dark:to-[#242526]"
      ></div>
    );
  };

  const changeStatus = () => {
    setOpen(true);
  };

  const handleLogoutPopUp = async () => {
    setLogoutPopUpOpen(!logoutPopUpOpen);
  };

  const logoImg = user?.profile.isHitachiOrg ? (
    <div className="flex items-center h-full">
      <img
        onContextMenu={(e) => e.preventDefault()}
        style={{
          userSelect: "none",
          cursor: "pointer"
        }}
        className="h-10"
        src={isDarkMode ? hitachiDarkLogo : hitachiLogo}
        alt="Hitachi"
      />
      <div aria-hidden="true" className="w-0.5 bg-[#D40000] h-9 mx-2"></div>
      <img
        onContextMenu={(e) => e.preventDefault()}
        style={{
          userSelect: "none",
          cursor: "pointer"
        }}
        className="h-8"
        src={isDarkMode ? glDarkLogo : glLogo}
        alt="Great Learning"
      />
    </div>
  ) : user?.profile.isTMobileOrg ? (
    <div className="flex items-center h-full">
      <img
        onContextMenu={(e) => e.preventDefault()}
        style={{
          userSelect: "none",
          cursor: "pointer"
        }}
        className="h-8"
        src={TMobileLogo}
        alt="T-Mobile"
      />
      <div aria-hidden="true" className="w-0.5 bg-[#E20074] h-9 mx-2"></div>
      <img
        onContextMenu={(e) => e.preventDefault()}
        style={{
          userSelect: "none",
          cursor: "pointer"
        }}
        className="h-8"
        src={isDarkMode ? glDarkLogo : glLogo}
        alt="Great Learning"
      />
    </div>
  ) : user?.profile.org == "glq" ? (
    <div className="flex items-center h-full">
      <img
        onContextMenu={(e) => e.preventDefault()}
        style={{
          userSelect: "none",
          cursor: "pointer"
        }}
        className="h-8"
        src={glqLogo}
        alt="Globallogic Skill Quotient Logo"
      />
    </div>
  ) : (
    <img
      onContextMenu={(e) => e.preventDefault()}
      style={{
        userSelect: "none",
        cursor: hasAssessmentRecOrgs ? "default" : "pointer"
      }}
      className="h-10"
      src={isDarkMode ? glxLogoDark : glxLogo}
      alt="GLX"
    />
  );

  return (
    <>
      <header
        role="banner"
        className={`flex items-center justify-between px-3 py-4 border-gray-300 dark:bg-secondary-bg shadow-md border-b-px sm:px-5 md:px-9 xl:px-16 
        ${location.pathname === "/" && "sticky top-0 z-[10000] bg-gray-50 dark:bg-secondary-bg bg-clip-padding backdrop-filter backdrop-blur dark:bg-opacity-50 bg-opacity-50 backdrop-saturate-100 backdrop-contrast-100"}`}
      >
        {hasAssessmentRecOrgs && !user?.profile?.isHitachiOrg ? (
          logoImg
        ) : (
          <Link
            aria-label="Go to home page"
            to={
              tenantConfig?.tos?.enable
                ? user?.complyToLatestTOS
                  ? "/"
                  : "#"
                : "/"
            }
          >
            {logoImg}
          </Link>
        )}
        {location.pathname !== "/tos" && (
          <div className="flex items-center gap-3 sm:gap-5 text-secondary">
            <VisibilityGuard
              NotVisibleCondition={
                hasAssessmentRecOrgs ||
                orgRoutes[currentOrg] !== "ALL" ||
                displaySearch === false
              }
            >
              <div className="relative">
                <button
                  type="button"
                  onClick={changeStatus}
                  aria-label="Open search box"
                  className="flex items-center justify-center text-secondary hover:text-primary focus:text-primary focus-visible:ring-2 focus-visible:ring-dark-widget rounded-md transition-colors"
                >
                  <MagnifyingGlassIcon aria-hidden="true" className="h-8" />
                </button>
                {searchParams.get("searchRequest") != null &&
                  searchParams.get("searchRequest") != "" && (
                    <div className="absolute rounded-full sm:size-3 size-2 bg-primary sm:ring-2 ring-1 ring-bkg dark:ring-secondary-bg -top-px right-1 pointer-events-none"></div>
                  )}
              </div>
            </VisibilityGuard>
            <VisibilityGuard
              NotVisibleCondition={
                hasAssessmentRecOrgs ||
                orgRoutes[currentOrg] !== "ALL" ||
                displaySearch === false
              }
            >
              <Link className="flex sm:hidden" to={"/search"}>
                <BookOpenIcon className="size-6" />
              </Link>
            </VisibilityGuard>

            <Link className="hidden" to={"/search"}>
              <BookOpenIcon aria-hidden="true" className="size-6" />
            </Link>
            <div className="flex sm:hidden">{divider()}</div>

            <SearchBox open={open} setOpen={setOpen} />
            <VisibilityGuard NotVisibleCondition={hasAssessmentRecOrgs}>
              <VisibilityGuard
                NotVisibleCondition={!tenantConfig?.header?.calendar?.show}
              >
                <Popover className="relative inline-block">
                  {({ open, close }: any) => (
                    <div className="flex ">
                      <Popover.Button
                        id="flyoutMenu"
                        aria-label="Open calendar agenda"
                        aria-expanded={open}
                        className={`inline-flex items-center text-sm font-semibold text-left text-secondary hover:text-primary focus:text-primary focus-visible:ring-2 focus-visible:ring-dark-widget rounded-md ${
                          open ? "outline-none text-primary" : "outline-none"
                        }`}
                        data-testid="flyoutMenuBtn"
                      >
                        <CalendarDaysIcon aria-hidden="true" className="h-8" />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel
                          aria-label="Calendar agenda"
                          className="absolute z-50 
                            -left-full
                            lg:left-1/3
                            sm:-translate-x-1/2
                            -translate-x-2/3
                            top-full
                            flex 
                            w-[318px]
                            mt-4
                            mr-4
                            md:min-w-[27rem]
                            md:w-auto
                            
                      "
                        >
                          <div className="flex-auto w-full overflow-hidden text-sm leading-6 shadow-lg bg-bkg rounded-3xl ring-1 ring-gray-900/5">
                            <CalendarAgendaCard />
                            <div className="grid grid-cols-2 bg-gray-200 divide-x divide-gray-300 dark:divide-secondary-bg dark:bg-profile-bkg ">
                              <Link
                                to={"/calendar/calendar-dashboard-learner"}
                                onClick={() => {
                                  close && close();
                                  track("onClick", {
                                    eventName: "onClick",
                                    object: {
                                      id: user.profile.id,
                                      objectType: "CALENDAR_SELF"
                                    },
                                    verb: {
                                      id: "VIEWED",
                                      display: {
                                        "en-US": "My Calendar"
                                      }
                                    }
                                  });
                                }}
                                className="flex items-center  justify-center gap-x-2.5 p-3 font-semibold text-secondary  hover:bg-gray-100 dark:hover:bg-gray-500 text-xs"
                                data-testid="myCalenderLink"
                              >
                                <CalendarDaysIcon
                                  aria-hidden="true"
                                  className="h-6"
                                />
                                <span className="text-xs font-semibold text-secondary">
                                  {"My Calendar"}
                                </span>
                              </Link>
                              <Link
                                to={"/calendar/calendar-dashboard-global"}
                                onClick={() => {
                                  close && close();
                                  track("onClick", {
                                    eventName: "onClick",
                                    object: {
                                      id: user.profile.id,
                                      objectType: "CALENDAR_GLOBAL"
                                    },
                                    verb: {
                                      id: "VIEWED",
                                      display: {
                                        "en-US": "Training Calendar"
                                      }
                                    }
                                  });
                                }}
                                className="flex items-center justify-center gap-x-2.5 p-3 font-semibold text-secondary hover:bg-gray-100 dark:hover:bg-gray-500 text-xs"
                                data-testid="trainingCalenderLink"
                              >
                                <GlobeAltIcon
                                  aria-hidden="true"
                                  className="h-6"
                                />
                                <span className="text-xs font-semibold text-secondary ">
                                  {"Training Calendar"}
                                </span>
                              </Link>
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </div>
                  )}
                </Popover>
              </VisibilityGuard>
              <div className="items-center hidden gap-4 sm:flex">
                {divider()}
                <Link
                  to={"/search"}
                  className="font-semibold hover:text-primary focus:text-primary focus-visible:ring-2 focus-visible:ring-dark-widget"
                >
                  {/* <span className="font-semibold hover:text-primary focus:text-primary focus-visible:ring-2 focus-visible:ring-dark-widget"> */}
                  Content Library
                  {/* </span> */}
                </Link>
                {/* <Menu as="div" className="relative inline-block text-left">
                <div>
                  <MenuButton className="flex items-center rounded-full hover:text-primary focus:outline-none">
                    <span className="font-semibold">Explore</span>
                    <ChevronDownIcon className="h-4 mx-1 cursor-pointer" />
                  </MenuButton>
                </div>

                <MenuItems
                  transition
                  className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-lg bg-bkg dark:bg-profile-bkg shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    <MenuItem>
                      <Link
                        to="/calendar/calendar-dashboard-global"
                        className="flex p-4 py-3 items-center text-sm text-gray-900 dark:text-gray-200 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-600 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      >
                        <CalendarDaysIcon
                          aria-hidden="true"
                          className="mr-3 size-6 text-gray-500 dark:text-gray-400 group-data-[focus]:text-gray-500"
                        />
                        Training Calendar
                      </Link>
                    </MenuItem>
                    <div className="w-full bg-gradient-to-r from-[#f5f5f5] via-[#c8cad3] to-[#f5f5f5] dark:from-[#242526] dark:via-[#808080] dark:to-[#242526] h-px  my-1"></div>
                    <MenuItem>
                      <Link
                        to="/calendar/calendar-dashboard-learner"
                        className="flex p-4 py-3 items-center text-sm text-gray-900 dark:text-gray-200 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-600 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      >
                        <CalendarDateRangeIcon
                          aria-hidden="true"
                          className="mr-3 size-6 text-gray-500 dark:text-gray-400 group-data-[focus]:text-gray-500"
                        />
                        My Calendar
                      </Link>
                    </MenuItem>
                    <div className="w-full bg-gradient-to-r from-[#f5f5f5] via-[#c8cad3] to-[#f5f5f5] dark:from-[#242526] dark:via-[#808080] dark:to-[#242526] h-px  my-1"></div>
                    <MenuItem>
                      <Link
                        to="/profile/activity-dashboard/overview-dashboard"
                        className="flex p-4 py-3 items-center text-sm text-gray-900 dark:text-gray-200 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-600 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      >
                        <ChartPieIcon
                          aria-hidden="true"
                          className="mr-3 size-6 text-gray-500 dark:text-gray-400 group-data-[focus]:text-gray-500"
                        />
                        Activity Dashboard
                      </Link>
                    </MenuItem>
                    <div className="w-full bg-gradient-to-r from-[#f5f5f5] via-[#c8cad3] to-[#f5f5f5] dark:from-[#242526] dark:via-[#808080] dark:to-[#242526] h-px  my-1"></div>

                    <form action="#" method="POST">
                      <MenuItem>
                        <Link
                          to="/profile/activity-dashboard/assigned-content"
                          className="flex p-4 py-3 items-center text-sm text-gray-900 dark:text-gray-200 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-600 data-[focus]:text-gray-900 data-[focus]:outline-none"
                        >
                          <ClipboardDocumentListIcon
                            aria-hidden="true"
                            className="mr-3 size-6 text-gray-500 dark:text-gray-400 group-data-[focus]:text-gray-500"
                          />
                          Assigned Content
                        </Link>
                      </MenuItem>
                    </form>
                    <div className="w-full bg-gradient-to-r from-[#f5f5f5] via-[#c8cad3] to-[#f5f5f5] dark:from-[#242526] dark:via-[#808080] dark:to-[#242526] h-px  my-1"></div>
                    <MenuItem>
                      <Link
                        to="/cafe-lounge"
                        className="flex p-4 py-3 items-center text-sm text-gray-900 dark:text-gray-200 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-600 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      >
                             <img
              onContextMenu={(e) => e.preventDefault()}
              style={{ userSelect: "none" }} className="mr-3 size-6" src={cafeLogo} />
                        Cafe Lounge
                      </Link>
                    </MenuItem>
                    {hasReportees && (
                      <>
                        <div className="w-full bg-gradient-to-r from-[#f5f5f5] via-[#c8cad3] to-[#f5f5f5] dark:from-[#242526] dark:via-[#808080] dark:to-[#242526] h-px  my-1"></div>
                        <MenuItem>
                          <Link
                            to="/profile/manager-dashboard"
                            className="flex p-4 py-3 items-center text-sm text-gray-900 dark:text-gray-200 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-600 data-[focus]:text-gray-900 data-[focus]:outline-none"
                          >
                            <RectangleGroupIcon
                              aria-hidden="true"
                              className="mr-3 size-6 text-gray-500 dark:text-gray-400 group-data-[focus]:text-gray-500"
                            />
                            Manager Dashboard
                          </Link>
                        </MenuItem>
                      </>
                    )}
                  </div>
                </MenuItems>
              </Menu>
              {divider()} */}
                <VisibilityGuard
                  NotVisibleCondition={!tenantConfig?.header?.exploreMenu?.show}
                >
                  <Popover className="relative hidden sm:inline-block">
                    <PopoverButton
                      aria-label="Explore features menu"
                      className="relative flex items-center justify-center size-9 rounded-full text-secondary hover:bg-gray-100 dark:hover:bg-profile-bkg focus:text-primary focus-visible:ring-2 focus-visible:ring-dark-widget rounded-md transition-colors"
                    >
                      {/* 9-dot waffle grid icon */}
                      <span className="grid grid-cols-3 gap-[3px] w-[18px]">
                        {Array.from({ length: 9 }).map((_, i) => (
                          <span
                            key={i}
                            aria-hidden="true"
                            className="block size-[4px] rounded-full bg-current"
                          />
                        ))}
                      </span>
                      {/* live badge */}
                      {/* <span className="absolute top-0 right-0 inline-flex rounded-full size-2.5 bg-primary">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                      </span> */}
                    </PopoverButton>

                    <PopoverPanel
                      aria-label="Explore features"
                      transition
                      className="absolute left-1/2 z-30 top-full mt-2 flex w-screen max-w-min -translate-x-2/3 px-4 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                    >
                      {({ close }: any) => (
                        <div
                          className="relative w-[360px] max-h-[80vh] overflow-y-auto thin-scrollbar p-4 font-medium shrink rounded-3xl border-8 border-gray-100 dark:border-zinc-600 bg-bkg text-secondary shadow-2xl"
                          style={{ scrollbarWidth: "thin" }}
                        >
                          <p className="text-lg font-semibold text-secondary px-1 pb-3 mb-3">
                            Explore Features
                          </p>
                          <div className="grid grid-cols-3 gap-1">
                            {exploreMenuItems
                              .filter((item) => !item.disable)
                              .map((item, id) =>
                                item.openInNewTab ? (
                                  <a
                                    key={id}
                                    href={item.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`${item.title} (opens in a new tab)`}
                                    className="flex group relative flex-col text-center justify-center items-center gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-profile-bkg transition-colors"
                                  >
                                    <img
                                      onContextMenu={(e) => e.preventDefault()}
                                      style={{ userSelect: "none" }}
                                      className="size-12 shrink-0 transition-transform duration-200 group-hover:scale-110"
                                      src={item.icon}
                                      alt=""
                                      aria-hidden="true"
                                    />
                                    <span className="text-xs leading-4 h-8 flex items-center justify-center text-center line-clamp-2 group-hover:text-primary transition-colors">
                                      {item.title}
                                    </span>
                                    {item.new && (
                                      <span className="absolute top-1 right-1 bg-[#fdcc03] text-black text-[10px] font-semibold rounded-full px-1.5 py-px">
                                        New
                                      </span>
                                    )}
                                  </a>
                                ) : (
                                  <Link
                                    key={id}
                                    to={item.to}
                                    onClick={close}
                                    className="flex group relative flex-col text-center justify-center items-center gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-profile-bkg transition-colors"
                                  >
                                    <img
                                      onContextMenu={(e) => e.preventDefault()}
                                      style={{ userSelect: "none" }}
                                      className="size-12 shrink-0 transition-transform duration-200 group-hover:scale-110"
                                      src={item.icon}
                                      alt=""
                                      aria-hidden="true"
                                    />
                                    <span className="text-xs leading-4 h-8 flex items-center justify-center text-center line-clamp-2 group-hover:text-primary transition-colors">
                                      {item.title}
                                    </span>
                                    {item.new && (
                                      <span className="absolute top-1 right-1 bg-[#fdcc03] text-black text-[10px] font-semibold rounded-full px-1.5 py-px">
                                        New
                                      </span>
                                    )}
                                  </Link>
                                )
                              )}
                          </div>
                        </div>
                      )}
                    </PopoverPanel>
                  </Popover>
                </VisibilityGuard>

                {divider()}
              </div>
            </VisibilityGuard>
            {(import.meta.env.MODE === "dev" ||
              import.meta.env.MODE === "staging") && (
              <div className="hidden sm:inline-block">
                <Switcher />
              </div>
            )}
            {subscriberId && !hasAssessmentRecOrgs && (
              <Notification
                subscriberId={subscriberId}
                expirationLimit={{ unit: "d", value: 3 }}
                mobileHref="/notifications"
              />
            )}
            <Popover className="relative hidden mx-4 ml-2 sm:inline-block">
              <PopoverButton
                aria-label={`Open user profile menu for ${user?.profile?.name?.fullName || "current user"}`}
                className="inline-flex items-center font-semibold rounded-full focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:secondary dark:ring-offset-secondary-bg focus:ring-offset-2"
              >
                <span
                  aria-hidden="true"
                  className="flex size-10 items-center justify-center rounded-full  bg-gradient-to-b from-[#9A9A9A] to-gray-500 dark:from-gray-700 dark:to-gray-800"
                >
                  <span className="font-semibold text-white ">
                    {user?.profile?.name?.firstName?.charAt(0)}
                    {user?.profile?.name?.lastName?.charAt(0)}
                  </span>
                </span>
              </PopoverButton>

              <PopoverPanel
                aria-label="User profile menu"
                transition
                className="absolute left-1/2 z-30 mt-3 flex w-screen max-w-min -translate-x-3/4 px-4 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
              >
                {({ close }: any) => (
                  <div className="w-56 p-3 text-sm font-medium shadow-lg shrink rounded-xl bg-bkg text-secondary ring-1 ring-secondary/5">
                    {menuItems.map((item) => (
                      <Link
                        key={item.name}
                        to={
                          item.name === getAuthoringLibraryLabel("logout") ||
                          item.href === "/"
                            ? `${location.pathname}${location.search}`
                            : item.href
                        }
                        onClick={
                          item.name === getAuthoringLibraryLabel("logout")
                            ? handleLogoutPopUp
                            : close
                        }
                        className={`flex focus:text-primary gap-x-4 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-profile-bkg font-medium text-secondary" ${item?.disabled && "hidden"}`}
                      >
                        <item.icon
                          aria-hidden="true"
                          className="flex-none text-gray-400 size-6"
                        />
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </PopoverPanel>
            </Popover>

            <Popover className="relative mx-4 hidden">
              {({ close }: any) => (
                <>
                  <PopoverButton
                    aria-label="Open navigation menu"
                    className="inline-flex items-center font-semibold rounded-full focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:secondary dark:ring-offset-secondary-bg focus:ring-offset-2"
                  >
                    <Bars3Icon aria-hidden="true" className="h-6 sm:h-8" />
                  </PopoverButton>

                  <PopoverPanel
                    transition
                    className="absolute -right-7 z-30 mt-5 flex w-screen transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <div className="w-full p-3 text-sm font-medium rounded-none shadow-lg shrink bg-bkg dark:bg-secondary-bg text-secondary ring-1 ring-secondary/5">
                      <div className="flex justify-between my-4">
                        <div className="flex gap-4">
                          <span
                            aria-hidden="true"
                            className="flex h-10 w-10 items-center justify-center rounded-full  bg-gradient-to-b from-[#9A9A9A] to-gray-500 dark:from-gray-700 dark:to-gray-800"
                          >
                            <span className="font-semibold text-white ">
                              {user?.profile?.name?.firstName?.charAt(0)}
                              {user?.profile?.name?.lastName?.charAt(0)}
                            </span>
                          </span>
                          <div className="flex flex-col">
                            <span className="font-semibold">
                              {user?.profile?.name?.fullName}
                            </span>
                            <span className="font-normal">
                              {user?.profile?.email}
                            </span>
                          </div>
                        </div>
                        {(!window.location.pathname.includes("assessment") ||
                          window.location.pathname.includes("authoring")) && (
                          <Switcher />
                        )}
                      </div>
                      <div className="h-px  my-1 w-full bg-gradient-to-r from-[#f5f5f5] via-[#c8cad3] to-[#f5f5f5] dark:from-[#242526] dark:via-[#808080] dark:to-[#242526]"></div>
                      {menuItems.map((item) => (
                        <Link
                          key={item.name}
                          to={
                            item.name ===
                              getAuthoringLibraryLabel("logout", language) ||
                            item.href === "/"
                              ? `${location.pathname}${location.search}`
                              : item.href
                          }
                          onClick={
                            item.name ===
                            getAuthoringLibraryLabel("logout", language)
                              ? handleLogoutPopUp
                              : close
                          }
                          className={`flex focus:text-primary gap-x-4 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-bkg-dark font-medium text-secondary" ${item?.disabled && "hidden"}`}
                        >
                          <item.icon
                            aria-hidden="true"
                            className="flex-none text-gray-400 size-6"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </PopoverPanel>
                </>
              )}
            </Popover>
          </div>
        )}
        <Modal
          dialogueClassName="sm:max-w-3xl"
          open={logoutPopUpOpen}
          setOpen={setLogoutPopUpOpen}
          children={
            <LogoutConfirmationModal
              setOpen={handleLogoutPopUp}
              onConfirm={() => {}}
            />
          }
        />
      </header>
    </>
  );
}
